import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { verticalLabel, type NetworkLocation, type NetworkLocationInput } from '@dmn/shared';
import { MapPin, Plus, Search, Trash2, Pencil } from 'lucide-react';
import { LocationDialog } from '@/components/LocationDialog';
import { RowAction, RowActions } from '@/components/RowActions';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api, ApiError } from '@/lib/api';
import { useGuideHost } from '@/lib/useGuideHost';
import { queryKeys } from '@/lib/queries';
import {} from '@/lib/utils';

export const Route = createFileRoute('/locations/')({ component: LocationsIndex });

/**
 * Practices, as DMN sees them.
 *
 * The dashboard owns the real record — this is the projection targeting reads.
 * Editing is allowed because nothing yet syncs from the dashboard, so without a
 * form a fresh environment has no inventory and no campaign can be eligible.
 * The dialog is explicit about the one field that matters: `id` has to equal the
 * dashboard's location id, because that is what host products send.
 *
 * Filtering happens client-side. The whole collection is already loaded — it is
 * a few hundred documents at pilot scale — so a server round trip per keystroke
 * would add latency without adding anything.
 */
function LocationsIndex() {
  const queryClient = useQueryClient();
  const guideHost = useGuideHost();
  const locations = useQuery({ queryKey: queryKeys.locations, queryFn: api.locations.list });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<NetworkLocation | null>(null);

  const save = useMutation({
    mutationFn: (input: NetworkLocationInput) => api.locations.upsert(input),
    onSuccess: async () => {
      setCreating(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.locations });
    },
  });

  // Memoised so the `?? []` fallback does not produce a fresh array identity on
  // every render and re-run the filter below for nothing.
  const all = useMemo(() => locations.data ?? [], [locations.data]);

  // Matches on everything an operator might have to hand: the practice name,
  // either id pasted from the dashboard, the slug from a guide URL, or the
  // vertical's display label. Now via the shared hook, so this view and the
  // eight tables that copied its behaviour cannot drift on what a match means.
  const search = useTableSearch(all, (location) => [
    location.name,
    location.id,
    location.accountId,
    location.slug,
    location.guideDomain,
    verticalLabel(location.vertical),
  ]);
  const filtered = search.filtered;

  const unprovisioned = all.filter((location) => !location.guideDomain).length;
  const adsOff = all.filter((location) => location.adsEnabled === false).length;

  const pagination = usePagination(filtered);
  const removeLocation = useMutation({
    mutationFn: (id: string) => api.locations.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.locations });
    },
  });

  return (
    <>
      <PageHeader
        title="Locations"
        description="Practices in the network — DMN's projection of the DearDoc dashboard"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            Add practice
          </Button>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search name, id, slug, domain or vertical"
          label="Search practices"
          noun="practice"
        />

        {(unprovisioned > 0 || adsOff > 0) && (
          <div className="space-y-1 text-sm text-muted-foreground">
            {unprovisioned > 0 && (
              <p>
                {unprovisioned} without a guide domain — these fall back to{' '}
                <code className="font-mono text-xs">
                  {guideHost ?? 'the guide host'}/&#123;slug&#125;
                </code>
                .
              </p>
            )}
            {adsOff > 0 && (
              <p>
                {adsOff} with sponsored content switched off — their guide pages still serve, the
                slot stays empty.
              </p>
            )}
          </div>
        )}

        <Card className="shadow-none">
          <CardContent className="p-0">
            {locations.isError ? (
              <ErrorState error={locations.error} onRetry={() => void locations.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Vertical</TableHead>
                    <TableHead>Guide domain</TableHead>
                    <TableHead>Ads</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-16 text-right">
                      <span className="sr-only">Edit</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locations.isPending && <LoadingRows columns={7} />}
                  {pagination.pageRows.map((location) => (
                    <TableRow key={location.id}>
                      <TableCell>
                        <p className="font-medium">{location.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {location.id} · /{location.slug}
                        </p>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {location.accountId}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {verticalLabel(location.vertical)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {location.guideDomain ?? <span className="italic">not provisioned</span>}
                      </TableCell>
                      <TableCell>
                        {/* Absent means enabled — locations predate this field. */}
                        {location.adsEnabled === false ? (
                          <Badge variant="outline" className="text-amber-600 dark:text-amber-400">
                            Off
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">On</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={location.status === 'active' ? 'default' : 'secondary'}>
                          {location.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-px text-right">
                        <RowActions>
                          <RowAction
                            icon={Pencil}
                            label={`Edit ${location.name}`}
                            onClick={() => setEditing(location)}
                          />
                          <RowAction
                            icon={Trash2}
                            label={`Delete ${location.name}`}
                            destructive
                            disabled={removeLocation.isPending}
                            onClick={() => {
                              if (
                                window.confirm(`Delete ${location.name}? This cannot be undone.`)
                              ) {
                                removeLocation.mutate(location.id);
                              }
                            }}
                          />
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="locations" />

            {!locations.isPending && all.length === 0 && (
              <EmptyState
                title="No practices yet"
                description="Nothing syncs from the dashboard yet, so add one here. Without at least one practice, no campaign can be eligible for anything."
                icon={MapPin}
                action={
                  <Button size="sm" onClick={() => setCreating(true)}>
                    <Plus className="size-4" />
                    Add practice
                  </Button>
                }
              />
            )}
            {!locations.isPending && all.length > 0 && filtered.length === 0 && (
              <EmptyState
                title="No matches"
                description={`Nothing matches “${search.query}”.`}
                icon={Search}
                action={
                  <Button size="sm" variant="outline" onClick={() => search.setQuery('')}>
                    Clear search
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>

        {save.isError && (
          <p className="text-sm text-destructive">
            {save.error instanceof ApiError ? save.error.message : 'Could not save this practice'}
          </p>
        )}
        {removeLocation.isError && (
          <p className="text-sm text-destructive">
            {removeLocation.error instanceof ApiError
              ? removeLocation.error.message
              : 'Could not delete'}
          </p>
        )}
      </PageBody>

      <LocationDialog
        open={creating || editing !== null}
        location={editing}
        pending={save.isPending}
        error={save.error}
        onSave={(input) => save.mutate(input)}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
            save.reset();
          }
        }}
      />
    </>
  );
}
